import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { piOtaReleaseService } from "../api/services/piOtaReleaseService";
import {
  PiOtaReleaseListResult,
  UploadPiOtaReleasePayload,
} from "../schemas/piOtaReleaseSchema";

const QUERY_KEY = ["pi-ota-releases"];

export const usePiOtaReleases = () => {
  const queryClient = useQueryClient();

  const {
    data: result,
    isLoading,
    refetch,
  } = useQuery<PiOtaReleaseListResult, Error>({
    queryKey: QUERY_KEY,
    queryFn: () => piOtaReleaseService.list(),
  });

  const releases = result?.releases ?? [];

  const uploadMutation = useMutation({
    mutationFn: (payload: UploadPiOtaReleasePayload) =>
      piOtaReleaseService.upload(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success("Release uploaded");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to upload release");
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => piOtaReleaseService.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success("Release activated — fleet will update");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to activate release");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => piOtaReleaseService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success("Release deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete release");
    },
  });

  return {
    releases,
    isLoading,
    refetch,
    uploadRelease: uploadMutation.mutateAsync,
    activateRelease: activateMutation.mutateAsync,
    deleteRelease: deleteMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    isActivating: activateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
